import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Sparkles, CheckCircle2 } from 'lucide-react';

/**
 * GenerationProgressOverlay
 * Floating bottom-right progress toast shown while any long-running AI
 * generation is active. Rotates through contextual "tick" messages and
 * displays elapsed time so users never feel the app has frozen.
 *
 * Props:
 *   isGenerating (bool): overlay visible while true
 *   label (string): report name being generated (e.g. "Movie Recommendations")
 *   ticks (string[]): optional custom rotating status messages
 */
const DEFAULT_TICKS = [
  'Reading client responses across all phases…',
  'Cross-referencing phase-level evidence…',
  'Synthesizing INSIGHT-native patterns…',
  'Grounding citations in raw client data…',
  'Formatting the final Analysis…',
  'Almost done — proofreading output…',
];

const GenerationProgressOverlay = ({ isGenerating, label = 'Analysis', ticks = DEFAULT_TICKS }) => {
  const [elapsed, setElapsed] = useState(0);
  const [tickIdx, setTickIdx] = useState(0);
  const timerRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (isGenerating) {
      setElapsed(0);
      setTickIdx(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      tickRef.current = setInterval(() => {
        setTickIdx((i) => (i + 1) % ticks.length);
      }, 4500);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [isGenerating, ticks.length]);

  if (!isGenerating) return null;

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const timeStr = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

  return (
    <div
      data-testid="generation-progress-overlay"
      className="fixed bottom-6 right-6 z-[9999] w-[340px] max-w-[92vw] rounded-2xl border border-blue-200 bg-white/95 shadow-2xl backdrop-blur-md animate-in slide-in-from-bottom-4"
      style={{ boxShadow: '0 20px 40px -12px rgba(59, 130, 246, 0.35)' }}
    >
      <div className="flex items-center gap-3 px-4 py-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-2xl">
        <div className="relative">
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
          <Sparkles className="w-3 h-3 text-indigo-400 absolute -top-1 -right-1 animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-blue-900 truncate">Generating {label}</p>
          <p className="text-xs text-blue-600 font-mono">{timeStr} elapsed</p>
        </div>
      </div>
      <div className="px-4 py-3 space-y-1.5">
        {ticks.map((t, i) => {
          const done = i < tickIdx;
          const active = i === tickIdx;
          return (
            <div
              key={i}
              className={`flex items-start gap-2 text-xs transition-all duration-300 ${
                done ? 'text-green-700' : active ? 'text-blue-800 font-medium' : 'text-gray-400'
              }`}
            >
              {done ? (
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              ) : active ? (
                <Loader2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 animate-spin" />
              ) : (
                <span className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 rounded-full border border-gray-300" />
              )}
              <span className="leading-snug">{t}</span>
            </div>
          );
        })}
      </div>
      <div className="px-4 pb-3">
        <p className="text-[10px] text-gray-500 italic">Long Analysis can take 30–90 seconds. Please keep this tab open.</p>
      </div>
    </div>
  );
};

export default GenerationProgressOverlay;
